import React, { useEffect, useState } from "react";
import { Container, Row } from "reactstrap";
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import AuditSummaryTable from './AuditSummaryTable'
import BreadCrumb from "Components/Common/BreadCrumb";
import AuditSummaryFilters from "./AuditSummaryFilters";
import { auditsSummaryGetRequest } from "store/audits/action";
import { usersListGetRequest } from "store/user/action";
import { roleListGetRequest } from "store/role/action";
import AuditSummaryCardSkeleton from "./AuditSummaryCardSkeleton";

interface Props {
}

const AuditSummaryCardList: React.FC<Props> = () => {
	const dispatch: any = useDispatch();

	const summaries = useSelector((state: ApplicationState) => state.audits.summaries);
	const loading = useSelector((state: ApplicationState) => state.audits.summariesLoading);
	const roles = useSelector((state: ApplicationState) => state.role.list);
	const users = useSelector((state: ApplicationState) => state.user.users);

	const today = new Date();

	const [startDate, setStartDate] = useState<Date>(today);
	const [endDate, setEndDate] = useState<Date>(today);
	const [selectedUser, setSelectedUser] = useState<string>("");
	const [selectedRole, setSelectedRole] = useState<string>("");
	const [hasLogsOnly, setHasLogsOnly] = useState<boolean>(false);

	useEffect(() => {
		dispatch(roleListGetRequest({}));
		dispatch(usersListGetRequest(`page=0&pageSize=5`));
	}, [])

	useEffect(() => {
		if (!startDate || !endDate) return;

		const startOfDay = new Date(startDate);
		startOfDay.setHours(0, 0, 0, 0);

		const endOfDay = new Date(endDate);
		endOfDay.setHours(23, 59, 59, 999);
		const filter = {
			quick: [
				...(startDate && endDate
					? [{
						key: "created_at",
						keyLabel: "Audit Date",
						condition: "BETWEEN",
						conditionLabel: "between",
						value: startOfDay.toISOString(),
						highValue: endOfDay.toISOString(),
						quick: true
					}]
					: []),
				...(selectedUser
					? [{
						key: "user",
						keyLabel: "User",
						condition: "IN",
						conditionLabel: "in",
						values: [selectedUser],
						quick: true
					}]
					: []),
				...(selectedRole
					? [{
						key: "role",
						keyLabel: "Role",
						condition: "IN",
						conditionLabel: "in",
						values: [selectedRole],
						quick: true
					}]
					: [])
			],
			advance: [],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			showOnlyWithLogs: hasLogsOnly
		};

		dispatch(auditsSummaryGetRequest(filter));
	}, [dispatch, startDate, endDate, selectedUser, selectedRole, hasLogsOnly]);

	const userOptions = users?.map((user: any) => ({
		value: user.id,
		label: user.name,
	})) || [];

	const roleOptions = roles?.map((role: any) => ({
		value: role.id,
		label: role.title,
	})) || [];

	return (
		<>
			{loading ? (
				<AuditSummaryCardSkeleton />
			) : (
				<Row>
					<AuditSummaryFilters
						startDate={startDate}
						endDate={endDate}
						selectedUser={selectedUser}
						selectedRole={selectedRole}
						userOptions={userOptions}
						roleOptions={roleOptions}
						onStartDateChange={setStartDate}
						onEndDateChange={setEndDate}
						onUserChange={setSelectedUser}
						onRoleChange={setSelectedRole}
						onReset={() => {
							setStartDate(today);
							setEndDate(today);
							setSelectedUser("");
							setSelectedRole("");
							setHasLogsOnly(false);
						}}
						hasLogsOnly={hasLogsOnly}
						onToggleHasLogsOnly={setHasLogsOnly}
					/>
					<AuditSummaryTable summaries={summaries || []} />
				</Row>
			)}
		</>
	);
};

export default AuditSummaryCardList;
